import React from 'react';
import TrashIcon from 'components/pure/utils/icon/TrashIcon';
import CheckboxField from 'components/pure/utils/form/CheckboxField';
import IconButton from 'material-ui/IconButton';
import bucketize from 'utils/bucketize';
import {PERMISSION_TYPES} from 'records/TriggerPermission';
import './RulePermissionsTable.styl';
import classnames from 'classnames';

export default class RulePermissionsTable extends React.PureComponent {
  getMemberLabel(group) {
    // All items in group are identical apart from permission type
    // So just use the first one
    const permission = group.get(0);
    if (permission.groupName) return permission.groupName;
    if (permission.accountId) {
      return 'Everyone';
    }
    if (permission.userId) {
      return this.getUserLabel(permission);
    }
    if (permission.departmentId) {
      return this.getDepartmentLabel(permission);
    }
    if (permission.teamId) {
      const team = this.props.teams.get(permission.teamId);
      if (!team) {
        console.warn(`could not find team ${permission.teamId} for permission label`);
      }
      return team ? team.name : 'Error retrieving team';
    }
  }

  getUserLabel(permission) {
    const user = this.props.users.get(permission.userId);
    return this.props.usersLoading.get(permission.userId)
      ? 'Loading...'
      : this.props.usersErrored.get(permission.userId)
      ? 'Error retrieving user'
      : user
      ? user.getFullName()
      : null;
  }

  getDepartmentLabel(permission) {
    const department = this.props.departmentProfiles.find(
      department => department.id === permission.departmentId
    );
    return this.props.areDepartmentProfilesLoading
      ? 'Loading...'
      : this.props.wasDepartmentProfilesFetchError
      ? 'Error retrieving department'
      : department
      ? department.name
      : null;
  }

  togglePermission(permission) {
    const {rulePermissions, onUpdate} = this.props;
    const index = rulePermissions.indexOf(permission);
    onUpdate(rulePermissions.setIn([index, 'active'], !permission.active));
  }

  createPermission(group, type) {
    const {rulePermissions, onUpdate} = this.props;
    onUpdate(rulePermissions.push(group.get(0).set('permission', type)));
  }

  deletePermissionGroup(group) {
    const {rulePermissions, onUpdate} = this.props;
    onUpdate(
      group
        .map(perm => rulePermissions.indexOf(perm))
        .reduce(
          (rulePermissions, index) =>
            // we display permissions with active = false, and only remove from the UI if they are deleted
            rulePermissions.setIn([index, 'active'], false).setIn([index, 'deleted'], true),
          rulePermissions
        )
    );
  }

  sortPermissions(groupA, groupB) {
    // Creation time first, last created on top, null created on bottom (existing ones)
    const aCreated = groupA.get(0).uiCreationTime;
    const bCreated = groupB.get(0).uiCreationTime;
    if (aCreated && bCreated) {
      // Should check for 0, but given this is the user clicking a button, they can't possibly be the same
      return bCreated.valueOf() - aCreated.valueOf();
    } else if (aCreated) {
      return -1;
    } else if (bCreated) {
      return 1;
    }
    const aTargetType = groupA.get(0).getSortIndex();
    const bTargetType = groupB.get(0).getSortIndex();
    // Entity hierarchy second (Everyone on top, then departments, then teams, then individuals)
    if (aTargetType !== bTargetType) {
      return aTargetType - bTargetType;
    }
    // Lastly by name
    const aGroupName = groupA.get(0).groupName;
    const bGroupName = groupB.get(0).groupName;
    if (aGroupName && bGroupName) {
      return aGroupName.localeCompare(bGroupName);
    } else if (aGroupName) {
      return 1;
    } else if (bGroupName) {
      return -1;
    } else {
      return 0;
    }
  }

  render() {
    const {rulePermissions, rule} = this.props;
    const thisUserPerms = rule.rulePermissions;
    const ownerId = rule.userId;
    const permissionColumns = PERMISSION_TYPES.filter(perm => {
      // Edit has full permissions, show SHARE even if the user does not have SHARE
      if (thisUserPerms.EDIT) {
        return true;
      }
      if (thisUserPerms.SHARE && perm.value === 'SHARE') {
        return true;
      } else {
        // For a user who only has SHARE, we don't even show the NOTIFY column
        return false;
      }
    });
    if (!rulePermissions) {
      return null;
    }
    const groupedPermissions = bucketize(
      rulePermissions.filter(permission => !permission.deleted),
      (a, b) => a.isSameTarget(b)
    );
    return (
      <table className="generic-table rule-permissions-table">
        <thead>
          <tr>
            <th>Members</th>
            {permissionColumns.map(type => (
              <th key={type.value} className="checkbox-column">
                {type.label}
              </th>
            ))}
            {thisUserPerms.EDIT && <th className="delete-column" />}
          </tr>
        </thead>
        <tbody>
          {groupedPermissions.sort(this.sortPermissions).map(group => (
            <tr key={group.hashCode()}>
              <td>{this.getMemberLabel(group)}</td>
              {permissionColumns
                .map(type => [type.value, group.find(({permission}) => permission === type.value)])
                .map(([type, permission]) => (
                  <td key={type}>
                    {
                      <CheckboxField
                        onClick={() =>
                          permission
                            ? this.togglePermission(permission)
                            : this.createPermission(group, type)
                        }
                        checked={permission && permission.active}
                        disabled={
                          permission &&
                          permission.userId === ownerId &&
                          (type === 'SHARE' || type === 'EDIT')
                        }
                      />
                    }
                  </td>
                ))}
              {thisUserPerms.EDIT && (
                <td>
                  <IconButton
                    disabled={group.get(0).userId === ownerId}
                    onClick={() => this.deletePermissionGroup(group)}
                    className={classnames(
                      'delete-button',
                      group.get(0).userId === ownerId ? 'disabled' : ''
                    )}>
                    <TrashIcon />
                  </IconButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}
